// ============================================================================
// AFB-0075 / PR 5 — the review an onboarding packet lands on, before anything
// is sent.
//
// It sits on the existing prep screen (/money/envelopes/:envelopeId) rather
// than being a second page: the assembled PDF is already rendered there, by the
// one renderer, and a review that showed a DIFFERENT document from the one the
// sender is about to send would be worse than no review at all. What this adds
// is everything the prep screen has no way to know about a packet:
//
//   * every merge value the resolver filled in — shown, and editable, because
//     the resolver's answer is a starting point and the sender is the one who
//     can see that an address is six months out of date;
//   * every gap, loudly. Unresolved values are listed as REFUSALS, not notes;
//   * every RESTRICTED field, by name and page and WITHOUT its answer. The
//     packet collects an SSN and a bank account; the sender needs to know that
//     and does not need to see them. Those answers live in esign_field_secrets
//     and this screen never reads that table;
//   * who it is addressed to, correctable and then explicitly confirmed;
//   * the covering email as the recipient will receive it.
//
// And it owns the send gate. A required merge field with nothing in it BLOCKS
// the send: a blank line on an executed I-9 cannot be taken back, and an error
// message can.
//
// The plan is recomputed here rather than carried in router state, so a reload
// — or a link pasted to somebody else — shows the same review rather than an
// empty one. esign_packet_plan writes nothing, so this is free to do.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Lock, Mail, ShieldAlert } from 'lucide-react';
import { errorMessage } from '../../shared/errors';
import {
  EnvelopeCopy, FieldRow, RecipientRow, listFieldBindings, saveRecipientContact,
  setFieldValue,
} from './envelope-lib';
import { FIELD_LABEL } from './signer-field';
import { packetPlan } from './packet-lib';
import {
  MergeFieldReview, PacketPlan, isMergeFieldResolved, mergeFieldsFromPlan,
  packetSendBlockers, parseDocumentOverrides, planBindingGapSentences,
  planGapSentences, restrictedFieldNotes, seedSignerValues,
} from './packet-launch';
import {
  MergeVariable, applyMerge, htmlToPreviewText,
} from './merge-vars';

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';

export interface PacketReviewProps {
  packetId: string;
  onboardingId: string;
  /** esign_envelopes.document_overrides, as read. */
  documentOverrides: unknown;
  envelopeTitle: string;
  senderName: string;
  expirationDays: number;
  copy: EnvelopeCopy;
  fields: FieldRow[];
  recipients: RecipientRow[];
  /** False once the envelope has been sent — the review becomes a record. */
  isDraft: boolean;
  /** Re-read the envelope after a write here. */
  onRefresh: () => void;
  /**
   * Reported up so the prep screen's Send button can refuse. Called with the
   * blockers as sentences; empty means the send may go ahead.
   */
  onGateChange: (blockers: string[]) => void;
}

export function PacketReview({
  packetId, onboardingId, documentOverrides, envelopeTitle, senderName,
  expirationDays, copy, fields, recipients, isDraft, onRefresh, onGateChange,
}: PacketReviewProps) {
  const [plan, setPlan] = useState<PacketPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planBusy, setPlanBusy] = useState(true);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const override = useMemo(
    () => parseDocumentOverrides(documentOverrides),
    [documentOverrides],
  );
  const includeKey = (override.include ?? []).join(',');
  const excludeKey = (override.exclude ?? []).join(',');

  // The plan for the list this envelope was ACTUALLY built from — the stored
  // override is replayed, or the review would describe a different packet.
  useEffect(() => {
    let cancelled = false;
    setPlanBusy(true);
    setPlanError(null);
    packetPlan({
      packet_id: packetId,
      onboarding_id: onboardingId,
      include: override.include,
      exclude: override.exclude,
    })
      .then((p) => { if (!cancelled) setPlan(p); })
      .catch((e) => {
        if (!cancelled) {
          setPlanError(errorMessage(
            e,
            'The packet behind this envelope could not be re-read, so its merge values ' +
              'cannot be checked here.',
          ));
        }
      })
      .finally(() => { if (!cancelled) setPlanBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId, onboardingId, includeKey, excludeKey]);

  useEffect(() => {
    let cancelled = false;
    listFieldBindings()
      .then((rows) => {
        if (cancelled) return;
        setLabels(Object.fromEntries(rows.map((r) => [r.binding_key, r.label])));
      })
      .catch(() => { /* labels are a nicety; the binding key is still readable */ });
    return () => { cancelled = true; };
  }, []);

  const mergeFields = useMemo(
    () => mergeFieldsFromPlan(plan, fields, labels),
    [plan, fields, labels],
  );

  // RESTRICTED fields — SSN, date of birth, bank account, routing. Listed so
  // the sender knows the packet collects them, and listed WITHOUT their
  // answers: the answers are in esign_field_secrets, which this screen does not
  // read and must not start reading. esign_fields.value is null on every one of
  // these by CHECK, so there is nothing here to accidentally render either.
  const restricted = useMemo(
    () => restrictedFieldNotes(plan, fields),
    [plan, fields],
  );

  // Drafts follow the SAME rule as the signer's own state (seedSignerValues):
  // seeded from the stored value when the fields arrive, never written over
  // what the person has typed. A refresh after a save must not take back an
  // edit they have made to the next field down.
  const [drafts, setDrafts] = useState<Record<string, string | boolean>>({});
  useEffect(() => {
    setDrafts((prev) => seedSignerValues(
      prev,
      mergeFields
        .filter((m): m is MergeFieldReview & { field_id: string } => m.field_id !== null)
        .map((m) => ({ field_id: m.field_id, field_type: 'text', value: m.value })),
    ));
  }, [mergeFields]);

  // Who the packet addressed itself to, correctable here. Seeded on arrival by
  // the same rule as the values above: a key already present belongs to the
  // person editing it, so a refresh cannot take their typing back.
  const [contacts, setContacts] =
    useState<Record<string, { name: string; email: string }>>({});
  useEffect(() => {
    setContacts((prev) => {
      let next: Record<string, { name: string; email: string }> | null = null;
      for (const r of recipients) {
        if (Object.prototype.hasOwnProperty.call(prev, r.recipient_id)) continue;
        next ??= { ...prev };
        next[r.recipient_id] = { name: r.name ?? '', email: r.email };
      }
      return next ?? prev;
    });
  }, [recipients]);

  const editContact = useCallback(
    (recipientId: string, patch: { name?: string; email?: string }) => {
      setContacts((prev) => ({
        ...prev,
        [recipientId]: { ...(prev[recipientId] ?? { name: '', email: '' }), ...patch },
      }));
      // Editing an address invalidates the tick that said the address was
      // checked — otherwise a confirmation could outlive the thing it confirmed.
      setConfirmed(false);
    },
    [],
  );

  async function saveContact(recipientId: string) {
    const contact = contacts[recipientId];
    if (!contact) return;
    setSaving(recipientId);
    setSaveError(null);
    try {
      await saveRecipientContact(recipientId, contact);
      onRefresh();
    } catch (e) {
      setSaveError(errorMessage(e, 'That recipient could not be saved.'));
    } finally {
      setSaving(null);
    }
  }

  async function saveValue(field: MergeFieldReview) {
    if (!field.field_id) return;
    setSaving(field.field_id);
    setSaveError(null);
    try {
      await setFieldValue(field.field_id, String(drafts[field.field_id] ?? ''));
      onRefresh();
    } catch (e) {
      setSaveError(errorMessage(e, 'That value could not be saved.'));
    } finally {
      setSaving(null);
    }
  }

  const gaps = useMemo(() => planGapSentences(plan), [plan]);
  const bindingGaps = useMemo(() => planBindingGapSentences(plan), [plan]);

  // The covering email, merged the same way the send confirmation merges it —
  // the sender should read the packet's own subject line here, not the default
  // one they would get if the packet had none.
  const firstSigner = recipients.find((r) => r.role !== 'CC');
  const vars: Partial<Record<MergeVariable, string>> = {
    recipient_name: firstSigner?.name ?? firstSigner?.email ?? '',
    document_name: envelopeTitle,
    sender_name: senderName,
    company_name: 'Terminal Cleaning Services',
    expiration_days: String(expirationDays),
    signing_link: 'https://app.terminalcleaning.com/sign/…',
  };
  const emailSubject = copy.email_subject?.trim()
    ? applyMerge(copy.email_subject, vars)
    : `Signature requested — ${envelopeTitle}`;
  const emailBody = copy.email_body?.trim()
    ? htmlToPreviewText(applyMerge(copy.email_body, vars))
    : `Hello ${firstSigner?.name ?? 'there'},\n\n${senderName} at Terminal Cleaning ` +
      `Services has requested your signature on ${envelopeTitle}.`;

  const blockers = useMemo(() => {
    if (!isDraft) return [];
    const base = packetSendBlockers({
      mergeFields,
      gaps,
      recipients: recipients
        .filter((r) => r.role !== 'CC')
        .map((r) => ({ name: r.name, email: r.email })),
      recipientsConfirmed: confirmed,
    });
    // A plan that could not be re-read is itself a refusal: without it there is
    // no way to know which fields were supposed to be prefilled, and "we could
    // not check" must never read as "there is nothing to check".
    if (planError) base.unshift(planError);
    if (planBusy && plan === null) {
      base.unshift('Still checking this packet’s merge values — hold on a moment.');
    }
    return base;
  }, [isDraft, mergeFields, gaps, recipients, confirmed, planError, planBusy, plan]);

  const blockerKey = blockers.join('|');
  useEffect(() => {
    onGateChange(blockers);
    // Keyed on the joined sentences rather than the array: a fresh array every
    // render would make this effect fire forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockerKey]);

  return (
    <section className="mb-4 rounded-md border border-tcs-border bg-tcs-raised p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-tcs-text">
            Onboarding packet review
          </h3>
          <p className="text-[11px] text-tcs-text-muted">
            {plan?.packet_name ?? packetId} · from onboarding {onboardingId}
            {plan ? ` · ${plan.documents.length} documents · ${plan.total_pages} pages` : ''}
            {override.include || override.exclude ? ' · document list overridden for this send' : ''}
          </p>
        </div>
        {isDraft ? (
          <span className="rounded-full bg-st-info-bg px-2 py-0.5 text-[10px] font-medium text-st-info-fg">
            Draft — nothing has been sent
          </span>
        ) : (
          <span className="rounded-full bg-tcs-surface px-2 py-0.5 text-[10px] font-medium text-tcs-text-secondary">
            Already sent — shown as a record
          </span>
        )}
      </div>

      {planBusy && plan === null && (
        <p className="mt-2 flex items-center gap-2 text-xs text-tcs-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Re-reading the packet behind this envelope…
        </p>
      )}
      {planError && (
        <p role="alert" className="mt-2 text-xs text-st-danger-fg">{planError}</p>
      )}

      {/* ── what is standing in the way, first and loudest ──────────────── */}
      {isDraft && blockers.length > 0 && (
        <div
          role="alert"
          className="mt-3 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-2"
        >
          <p className="flex items-start gap-1.5 text-[11px] font-semibold text-st-danger-fg-strong">
            <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {blockers.length} thing{blockers.length === 1 ? '' : 's'} to settle before this
            can be sent
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[11px] text-st-danger-fg-strong">
            {blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      )}
      {isDraft && blockers.length === 0 && plan !== null && (
        <p className="mt-3 flex items-center gap-1.5 rounded-md border border-st-ok-border-strong bg-st-ok-bg p-2 text-[11px] font-medium text-st-ok-fg-strong">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Everything the packet needs is filled in. Sending is a separate click —
          use “Send for signature” above.
        </p>
      )}

      {/* Values the resolver could not find. Shown, never used as a refusal on
          their own: the resolver walks EVERY registered binding, so most of
          these are values nothing on this packet asks for. The ones a document
          really uses are already listed above as blockers. */}
      {bindingGaps.length > 0 && (
        <div className="mt-3 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
          <p className="text-[11px] font-semibold text-st-warn-fg-strong">
            {bindingGaps.length} value{bindingGaps.length === 1 ? '' : 's'} could not be
            looked up for this person
          </p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[11px] text-st-warn-fg-strong">
            {bindingGaps.map((g) => <li key={g}>{g}</li>)}
          </ul>
          <p className="mt-1 text-[11px] text-st-warn-fg-strong">
            Only the ones a document in this packet actually uses block the send —
            those appear as empty required values below.
          </p>
        </div>
      )}

      {/* ── the prefilled values ────────────────────────────────────────── */}
      <div className="mt-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-secondary">
          Prefilled values ({mergeFields.length})
        </h4>
        <p className="mt-0.5 text-[11px] text-tcs-text-muted">
          These are written onto the documents before they are sent. Correct
          anything that is wrong — the change lands on this envelope only, not on
          the employee record.
        </p>
        {mergeFields.length === 0 ? (
          <p className="mt-1 text-[11px] italic text-tcs-text-muted">
            {plan === null
              ? 'Not read yet.'
              : 'No document in this packet has a merge field, so nothing is prefilled.'}
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1.5">
            {mergeFields.map((m) => {
              const key = m.field_id ?? `${m.binding}-${m.document_name}-${m.page}`;
              const resolved = isMergeFieldResolved(m);
              const draft = String(drafts[m.field_id ?? ''] ?? '');
              const dirty = m.field_id !== null && draft.trim() !== (m.value ?? '').trim();
              return (
                <li
                  key={key}
                  className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-1">
                    <span className={LABEL}>
                      {m.label}
                      {m.required && (
                        <span className="ml-1 font-bold text-st-danger-fg" aria-hidden>*</span>
                      )}
                    </span>
                    <span className="text-[10px] text-tcs-text-muted">
                      {m.document_name} · page {m.page}
                      {m.binding ? ` · ${m.binding}` : ''}
                    </span>
                  </div>
                  {m.field_id === null ? (
                    <p className="mt-1 text-[11px] text-st-danger-fg-strong">
                      No field on the built envelope matches this one. Regenerate
                      the packet rather than sending this envelope.
                    </p>
                  ) : (
                    <div className="mt-1 flex items-center gap-1.5">
                      <input
                        value={draft}
                        disabled={!isDraft || saving === m.field_id}
                        aria-label={`${m.label} on ${m.document_name}`}
                        placeholder={m.required ? 'Required — nothing on file' : 'Optional'}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDrafts((prev) => ({ ...prev, [m.field_id as string]: v }));
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveValue(m); }}
                        className={INPUT}
                      />
                      <button
                        type="button"
                        onClick={() => void saveValue(m)}
                        disabled={!isDraft || !dirty || saving === m.field_id}
                        className="min-h-[32px] shrink-0 rounded-md border border-tcs-border px-2 text-[11px] font-medium text-tcs-text disabled:opacity-40"
                      >
                        {saving === m.field_id ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                      </button>
                    </div>
                  )}
                  {m.field_id !== null && !resolved && (
                    <p className="mt-0.5 text-[10px] text-st-danger-fg">
                      {m.required
                        ? 'Empty — this would print as a blank on a signed document, so it blocks the send.'
                        : 'Empty. Optional, so it will simply be left blank.'}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {saveError && (
          <p role="alert" className="mt-1 text-[11px] text-st-danger-fg">{saveError}</p>
        )}
      </div>

      {/* ── restricted fields: named, never shown ───────────────────────── */}
      {restricted.length > 0 && (
        <div className="mt-3">
          <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-secondary">
            <Lock className="h-3.5 w-3.5" aria-hidden />
            Restricted ({restricted.length})
          </h4>
          <p className="mt-0.5 text-[11px] text-tcs-text-muted">
            The employee fills {restricted.length === 1 ? 'this one' : 'these'} in
            themselves — nothing is prefilled and nothing can be corrected here.
            Once {restricted.length === 1 ? 'it comes' : 'they come'} back the
            {restricted.length === 1 ? ' answer is' : ' answers are'} visible only
            to HR and executives, so {restricted.length === 1 ? 'it is' : 'they are'}{' '}
            not shown on this screen.
          </p>
          <ul className="mt-1.5 space-y-1">
            {restricted.map((r) => (
              <li
                key={r.field_id}
                className="flex flex-wrap items-baseline justify-between gap-1 rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5"
              >
                <span className={LABEL}>
                  {FIELD_LABEL[r.field_type as keyof typeof FIELD_LABEL] ?? 'Text'}
                  {r.required && (
                    <span className="ml-1 font-bold text-st-danger-fg" aria-hidden>*</span>
                  )}
                </span>
                <span className="text-[10px] text-tcs-text-muted">
                  {r.document_name ? `${r.document_name} · ` : ''}page {r.page}
                </span>
                <span className="rounded-full bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
                  Restricted — not shown
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── who it is going to ──────────────────────────────────────────── */}
      <div className="mt-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-secondary">
          Going to
        </h4>
        <ul className="mt-1.5 space-y-1.5">
          {recipients.map((r) => {
            const contact = contacts[r.recipient_id] ?? { name: r.name ?? '', email: r.email };
            const changed =
              contact.name.trim() !== (r.name ?? '').trim() ||
              contact.email.trim().toLowerCase() !== r.email.trim().toLowerCase();
            return (
              <li
                key={r.recipient_id}
                className="rounded-md border border-tcs-border bg-tcs-bg px-2 py-1.5"
              >
                <p className="text-[10px] text-tcs-text-muted">
                  {r.role}{r.role !== 'CC' ? ` · order ${r.signing_order}` : ''} · {r.status}
                </p>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
                  <input
                    value={contact.name}
                    disabled={!isDraft}
                    aria-label={`Name for ${r.email}`}
                    placeholder="Name"
                    onChange={(e) => editContact(r.recipient_id, { name: e.target.value })}
                    className={INPUT}
                  />
                  <input
                    value={contact.email}
                    disabled={!isDraft}
                    type="email"
                    aria-label={`Email for ${r.name ?? r.email}`}
                    placeholder="Email"
                    onChange={(e) => editContact(r.recipient_id, { email: e.target.value })}
                    className={INPUT}
                  />
                </div>
                {changed && isDraft && (
                  <button
                    type="button"
                    onClick={() => void saveContact(r.recipient_id)}
                    disabled={saving === r.recipient_id}
                    className="mt-1 min-h-[32px] rounded-md border border-tcs-border px-2 text-[11px] font-medium text-tcs-text disabled:opacity-40"
                  >
                    {saving === r.recipient_id ? 'Saving…' : 'Save this recipient'}
                  </button>
                )}
              </li>
            );
          })}
          {recipients.length === 0 && (
            <li className="text-[11px] italic text-tcs-text-muted">
              Nobody is on this envelope yet.
            </li>
          )}
        </ul>
        {isDraft && recipients.length > 0 && (
          <label className="mt-1.5 flex items-start gap-2 text-[11px] text-tcs-text">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <span>
              I have checked the name and email above. This is who receives the
              signed documents.
            </span>
          </label>
        )}
      </div>

      {/* ── the covering email ──────────────────────────────────────────── */}
      <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg p-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
          <Mail className="h-3.5 w-3.5" aria-hidden />
          What they&rsquo;ll receive
        </p>
        <p className="mt-1 text-xs font-semibold text-tcs-text">{emailSubject}</p>
        <p className="mt-0.5 whitespace-pre-wrap text-[11px] text-tcs-text-muted">
          {emailBody}
        </p>
        {copy.note_to_signer?.trim() && (
          <div className="mt-1.5 border-l-4 border-tcs-accent-ink bg-tcs-surface px-2 py-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              Note from {senderName}
            </p>
            <p className="whitespace-pre-wrap text-[11px] text-tcs-text">
              {copy.note_to_signer}
            </p>
          </div>
        )}
        <p className="mt-1 text-[10px] text-tcs-text-muted">
          Edit this in the email composer below. Links last {expirationDays} days.
        </p>
      </div>

      {/* ── what is in the packet ───────────────────────────────────────── */}
      {plan && plan.documents.length > 0 && (
        <ol className="mt-3 space-y-0.5">
          {plan.documents.map((d, i) => (
            <li key={d.template_id} className="text-[11px] text-tcs-text-muted">
              {i + 1}. <span className="text-tcs-text">{d.name}</span> ·{' '}
              {d.page_count === 1
                ? `page ${d.page_offset + 1}`
                : `pages ${d.page_offset + 1}–${d.page_offset + d.page_count}`}
              {d.checklist_key ? ` · ticks ${d.checklist_key}` : ''}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
